// One choice out of a handful, with the chosen one explaining itself.
//
// Three of these on the front door — the piece set, the board, how hard a run
// gets — and all three were a row of chips carrying a native `title`. The kit's
// `Segmented` is the row; the sentence goes *under* it rather than on a hover,
// because at this size the reader is comparing three words and the one fact
// they want is what the thing they have picked actually is.
//
// **Fewer words, not fewer facts**: the detail line is one sentence and it
// changes with the pick, so nothing is on screen twice.

import { Segmented, SectionTitle, Text, styled } from "../kit";

const Stack = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.kit.space.xs
}));

export interface ChipOption<T extends string> {
  id: T;
  name: string;
  detail: string;
}

export function ChipChoice<T extends string>({
  label,
  value,
  onChange,
  options,
  testPrefix
}: {
  /** The small-caps cap over the row. Omitted where the door's title says it. */
  label?: string;
  value: T;
  onChange(value: T): void;
  options: readonly ChipOption<T>[];
  /** Every chip's test id is `<prefix>-<id>`, and those ids are API. */
  testPrefix: string;
}) {
  const chosen = options.find((option) => option.id === value);
  return (
    <Stack>
      {label && <SectionTitle>{label}</SectionTitle>}
      <Segmented
        small
        value={value}
        onChange={onChange}
        options={options.map((option) => ({
          value: option.id,
          label: option.name,
          testId: `${testPrefix}-${option.id}`
        }))}
      />
      {chosen && (
        <Text tone="dim" size={12} block style={{ lineHeight: 1.45 }}>
          {chosen.detail}
        </Text>
      )}
    </Stack>
  );
}
